import { ReactNode } from 'react';
import i18n from 'i18next';
import { Gem } from 'lucide-react';
import { UserInfo } from '../network/httpClients/OIDCAuthenticationClient/api.schema';
import {
  PERMISSIONS,
  TIER_LEVELS,
  TierLevel,
  User,
} from '../network/httpClients/PlatformClient/api.schema';
import { OrganizationMemberData } from '../pages/Organization/types';

type TrialUserCheckResponse = {
  isTrialUser: boolean;
  dayLeftForTrial: number;
};

const DEFAULT_TRIAL_STATUS = {
  isTrialUser: false,
  dayLeftForTrial: 0,
} as const;

/**
 * Checks if the user is a trial user
 * @param user The user object
 * @returns An object containing the isTrialUser and dayLeftForTrial properties
 */
export const getUserTrialStatus = (
  user: User | undefined,
): TrialUserCheckResponse => {
  if (!user) {
    return DEFAULT_TRIAL_STATUS;
  }

  const { tierEndDate, tierLevel } = user;

  if (!!tierEndDate && tierLevel === TIER_LEVELS.TRIAL) {
    const now = new Date();
    const convertedTierEndDate = new Date(tierEndDate);
    const diffTime = convertedTierEndDate.getTime() - now.getTime();

    const dayLeftForTrial = Math.max(
      Math.ceil(diffTime / (1000 * 60 * 60 * 24)),
      0,
    );
    const isTrialUser = dayLeftForTrial > 0;

    return { isTrialUser, dayLeftForTrial };
  }

  return DEFAULT_TRIAL_STATUS;
};

/**
 * Checks if the user has a free tier level
 * @param user The user object
 * @returns True if the user has a free tier level, false otherwise
 */
export const isFreeUser = (user: User | undefined) =>
  user?.tierLevel === TIER_LEVELS.FREE;

/**
 * Checks if the user has a paying tier level
 * @param user The user object
 * @returns True if the user has a paying tier level, false otherwise
 */
export const isUserPaying = (user: User | undefined): boolean =>
  !!user?.tierLevel && user.tierLevel >= TIER_LEVELS.TRIAL;

/**
 * Checks if the user is an organization owner
 * @param user The user object
 * @returns True if the user is an organization owner, false otherwise
 */
export const isOrganizationOwner = (user: User | undefined) =>
  isEnterpriseUser(user) && user?.organizationPermission === PERMISSIONS.OWNER;

/**
 * Checks if the user belongs to a team
 * @param user The user object
 * @returns True if the user belongs to a team, false otherwise
 */
export const doesUserBelongsToTeam = (user: User | undefined) =>
  !!user?.teams && user.teams.length > 0;

/**
 * Checks if the user is a team owner
 * @param user The user object
 * @returns True if the user is a team owner, false otherwise
 */
export const isTeamOwner = (user: User | undefined) =>
  doesUserBelongsToTeam(user) &&
  !!user?.teams?.some((team) => team.permission === PERMISSIONS.OWNER);

/**
 * Checks if the user has an enterprise tier level
 * @param user The user object
 * @returns True if the user has an enterprise tier level, false otherwise
 */
export const isEnterpriseUser = (user: User | undefined) =>
  user?.tierLevel === TIER_LEVELS.ENTERPRISE;

/**
 * Checks if the user is able to invite members
 * @param user The user object
 * @returns True if the user is able to invite members, false otherwise
 */
export const canInviteMembers = (user: User | undefined) =>
  isOrganizationOwner(user) || isTeamOwner(user);

/**
 * Checks if the user has a paying team tier level
 * @param user The user object
 * @returns True if the user has a paid tier level, false otherwise
 */
export const isPayingTeamUser = (user: User | undefined) =>
  user?.tierLevel === TIER_LEVELS.PAID;

/**
 * Gets the team ID from the user object
 * @param user The user object
 * @returns The team ID
 */
export const getTeamId = (user: User | undefined) => user?.teams?.[0]?.teamId;

/**
 * Checks if the user is a tier free user
 * @param user The user object
 * @returns True if the user is a tier free user, false otherwise
 */
export const isTierScopedOwner = (
  user: User | undefined,
  isFreePlansOnly: boolean = false,
) => {
  const { tierLevel } = user || {};
  let isTierScopedOwner = false;

  switch (tierLevel) {
    case TIER_LEVELS.FREE:
      isTierScopedOwner = doesUserBelongsToTeam(user)
        ? isTeamOwner(user)
        : true;
      break;

    case TIER_LEVELS.TRIAL:
    case TIER_LEVELS.PAID:
      // Mirror the FREE case: a solo (teamless) self-serve paid or trial user is
      // their own owner; only require team ownership when they actually belong
      // to a team. Without this, a converted self-serve user is bounced off
      // billing. Trial follows the same self-serve ownership rule as paid.
      isTierScopedOwner =
        !isFreePlansOnly &&
        (doesUserBelongsToTeam(user) ? isTeamOwner(user) : true);
      break;

    case TIER_LEVELS.ENTERPRISE:
      isTierScopedOwner = !isFreePlansOnly && isOrganizationOwner(user);
      break;

    default:
      isTierScopedOwner = false;
  }

  return isTierScopedOwner;
};

/**
 * Checks if the user may administer config-class surfaces (config, rules, skills,
 * Team Profiles, integrations, repo management, cross-repo). Mirrors the backend
 * `is_admin`: a tier-scoped owner OR a delegated team admin. Owner-only surfaces
 * (members, billing, org settings) stay gated on the raw owner predicates.
 * @param user The user object
 * @returns True if the user may administer config-class surfaces, false otherwise
 */
export const isEffectiveConfigAdmin = (user: User | undefined): boolean =>
  isTierScopedOwner(user) ||
  (user?.isDelegatedTeamAdmin ?? false) ||
  // Scoped member grant (edit_permission_level=member): unlocks the admin
  // pages' edit UI without implying the admin role.
  (user?.canEditAdminPages ?? false);

/**
 * Checks if the user is allowed to see the plans page.
 * User can see the plans page if they are not an enterprise user and are a tier scoped owner.
 * @param user The user object
 * @returns True if the user is allowed to see the plans page, false otherwise
 */
export const isPlansPageAllowed = (
  user: User | undefined,
  isFreePlansOnly: boolean = false,
) => !isEnterpriseUser(user) && isTierScopedOwner(user, isFreePlansOnly);

/**
 * Gets the text representation of a tier level
 * @param tierLevel The tier level number
 * @returns The text representation of the tier level
 */
export const getTierText = (tierLevel: TierLevel | undefined): string => {
  switch (tierLevel) {
    case TIER_LEVELS.ENTERPRISE:
      return i18n.t('general.tier-enterprise');
    case TIER_LEVELS.PAID:
      return i18n.t('general.tier-teams');
    case TIER_LEVELS.TRIAL:
    case TIER_LEVELS.FREE:
      return i18n.t('general.tier-free');
    default:
      return i18n.t('general.tier-developer');
  }
};

/**
 * Gets the tier component with optional icon for display
 * @param tierLevel The tier level to get the component for
 * @returns ReactNode - A span with icon and text for Enterprise tier, or just text for other tiers
 */
export const getTierComponent = (
  tierLevel: TierLevel | undefined,
): ReactNode => {
  switch (tierLevel) {
    case TIER_LEVELS.ENTERPRISE:
      return (
        <span className="flex items-center gap-2">
          <Gem className="size-4" />
          {getTierText(tierLevel)}
        </span>
      );
    default:
      return getTierText(tierLevel);
  }
};

/**
 * Transforms UserInfo data to User format, for supporting OIDC Authentication without refactoring the auth context structure
 * @param data The UserInfo object from OIDC Authentication API
 * @returns Partial User object with transformed data
 */
export const transformUserInfoToUser = (data: UserInfo): Partial<User> => ({
  email: data.email,
  name: data.displayName,
  avatar: data.photoURL,
  // Prefer the authoritative top-level tier (from the `tier_level` claim). Fall
  // back to metadata only when top-level is absent — GitHub-only / airgapped
  // OIDC modes set tier solely in metadata. Firebase always sets top-level, so
  // its buggy metadata.tierLevel=30 default never wins (PLT-4142).
  tierLevel: data.tierLevel ?? data.metadata?.tierLevel,
  tierEndDate: (data.tierEndDate ?? data.metadata?.tierEndDate)?.toString(),
  allowed: data.metadata?.allowed,
  codiumate: data.metadata?.codiumate,
});

/**
 * Transforms User data to UserInfo (OIDC) format - inverse of transformUserInfoToUser
 * @param user The User object from Platform API
 * @returns Partial UserInfo object for OIDC Authentication API
 */
export const transformUserToUserInfo = (user: User): Partial<UserInfo> => ({
  email: user.email,
  displayName: user.name,
  photoURL: user.avatar,
  metadata: {
    tierLevel: user.tierLevel,
    tierEndDate: user.tierEndDate ? Number(user.tierEndDate) : Date.now(),
    allowed: user.allowed,
    codiumate: false,
  },
});

/**
 * Checks if a user is the owner of a specific team
 * @param user - The user object
 * @param teamId - The team ID to check
 * @returns true if the user is the owner of the team
 */
export const isUserTeamOwnerOfSpecificTeam = (
  user: User | undefined,
  teamId: string | undefined,
) => isTeamOwner(user) && getTeamId(user) === teamId;

/**
 * Checks if a user can create a team based on their tier level and team membership
 * @param user - The user object
 * @returns true if the user is allowed to create a team, false otherwise
 *
 * Rules:
 * - Tier 30 (Enterprise): Only organization admins can create teams
 * - Tier 20 (Paid): Only single members without a team can create their first team
 * - Tier 1 (Free): Only single members without a team can create their first team
 * - Team members (any tier) cannot create teams
 * - Tier 30 regular members without teams cannot create teams
 */
export const canUserCreateTeam = (user: User | undefined): boolean => {
  if (!user) {
    return false;
  }

  const { tierLevel } = user;

  switch (tierLevel) {
    case TIER_LEVELS.ENTERPRISE:
      return isOrganizationOwner(user);

    case TIER_LEVELS.PAID:
    case TIER_LEVELS.FREE:
      return !doesUserBelongsToTeam(user);

    default:
      return false;
  }
};

/**
 * Checks if the current user can delete himself from the team/organization
 * @param currentUser - The current user object
 * @param member - The member to be removed
 * @returns true if the current user can delete himself, false otherwise
 **/
export const canUserRemoveHimselfFromTeam = (
  currentUser: User | undefined,
  member: OrganizationMemberData | undefined,
): boolean => {
  const isSelfRemoval = currentUser?.id === member?.id;

  if (!currentUser || !isSelfRemoval) {
    return false;
  }

  switch (currentUser.tierLevel) {
    case TIER_LEVELS.ENTERPRISE:
      return (
        isOrganizationOwner(currentUser) ||
        isUserTeamOwnerOfSpecificTeam(currentUser, member?.team?.id as string)
      );

    case TIER_LEVELS.PAID:
      return isUserTeamOwnerOfSpecificTeam(
        currentUser,
        member?.team?.id as string,
      );

    case TIER_LEVELS.FREE:
      return doesUserBelongsToTeam(currentUser);

    default:
      return false;
  }
};

/**
 * Checks if the current user can delete members from the team/organization
 * @param currentUser - The current user object
 * @param member - The member to be deleted
 * @returns true if the current user can delete members, false otherwise
 **/
export const canUserRemoveMembersFromTeam = (
  currentUser: User | undefined,
  member: OrganizationMemberData | undefined,
): boolean => {
  if (!currentUser || !member) {
    return false;
  }

  switch (currentUser.tierLevel) {
    case TIER_LEVELS.ENTERPRISE:
      return (
        isOrganizationOwner(currentUser) ||
        isUserTeamOwnerOfSpecificTeam(currentUser, member?.team?.id as string)
      );

    case TIER_LEVELS.PAID:
    case TIER_LEVELS.FREE:
      return isUserTeamOwnerOfSpecificTeam(
        currentUser,
        member?.team?.id as string,
      );

    default:
      return false;
  }
};

/**
 * Checks if the current user can update members in the team/organization
 * @param currentUser - The current user object
 * @param member - The member to be updated
 * @returns true if the current user can update members, false otherwise
 **/
export const canUserUpdateMembers = (
  currentUser: User | undefined,
  member: OrganizationMemberData | undefined,
): boolean => {
  if (!currentUser || !member) {
    return false;
  }

  switch (currentUser.tierLevel) {
    case TIER_LEVELS.ENTERPRISE:
      return (
        isOrganizationOwner(currentUser) ||
        isUserTeamOwnerOfSpecificTeam(currentUser, member?.team?.id as string)
      );

    case TIER_LEVELS.PAID:
    case TIER_LEVELS.FREE:
      return isUserTeamOwnerOfSpecificTeam(
        currentUser,
        member?.team?.id as string,
      );

    default:
      return false;
  }
};
